import { useState } from "react";

import ModalCloseIcon from "@/components/icons/modal-close-icon";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

import Filters from "../filters";
import useSelectedCategories from "../hooks/use-selected-categories";

export interface FiltersModalProps extends React.ComponentProps<typeof Dialog> {
  onClear(skipIncludeFilter?: boolean): void;
  onSubmit(form: FormData): void;
}

export default function FiltersModal({
  onClear,
  onSubmit,
  ...props
}: FiltersModalProps) {
  const [changed, setChanged] = useState(false);

  const { includeLabel } = useSelectedCategories();

  const handleClear = () => {
    onClear(true);
    props.onOpenChange?.(false);
  };

  const handleFormChange = () => setChanged(true);

  const handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit(new FormData(event.currentTarget));
    props.onOpenChange?.(false);
  };

  if (!props.open) {
    return null;
  }

  return (
    <Dialog {...props}>
      <DialogContent
        className="mt-33.5 ml-6 rounded-[15px] bg-twilight-indigo shadow-0-0-8 shadow-black/25 md:w-[calc(50%-3rem)] md:max-w-auto"
        overlayClassName="md:justify-start md:items-start"
      >
        <form
          autoComplete="off"
          autoCorrect="off"
          onChange={handleFormChange}
          onSubmit={handleSubmit}
          spellCheck="false"
        >
          <DialogHeader className="relative flex h-[65px] items-center justify-center border-white/10 border-b p-0">
            <Button
              aria-label="Close"
              className="absolute top-5 left-6 flex size-[25px] items-center justify-center rounded-full border border-white"
              onClick={() => props.onOpenChange?.(false)}
              type="button"
            >
              <ModalCloseIcon />
            </Button>
            <DialogTitle className="font-semibold text-16 text-white">
              Filter {includeLabel.toLocaleLowerCase()}
            </DialogTitle>
            <Button
              className={cn(
                "absolute top-0 right-0 h-full px-[35px] font-semibold text-13 text-white transition-opacity",
                changed ? "opacity-100" : "opacity-0"
              )}
              type="submit"
            >
              Apply
            </Button>
          </DialogHeader>
          <div className="p-6">
            <Filters onChange={handleFormChange} />
          </div>
          <DialogFooter className="flex items-center border-white/10 border-t px-6 py-5 sm:justify-between">
            <Button
              className="font-medium text-14 text-white underline hover:no-underline"
              onClick={handleClear}
              type="button"
            >
              Clear all
            </Button>
            <Button
              className={cn(
                "h-11 w-50 rounded-[22px] transition-opacity",
                changed ? "opacity-100" : "opacity-0"
              )}
              type="submit"
              variant="cta"
            >
              Apply
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
